import React, { useState } from 'react';
import { ShieldCheck, MapPin, BarChart3, ArrowRight, Lock } from 'lucide-react';
import { useDialogA11y } from '../lib/useDialogA11y';

interface TermsConsentModalProps {
  isOpen: boolean;
  onAccept: (analyticsConsent: boolean) => Promise<void>;
  onOpenTerms: () => void;
  onOpenPrivacy: () => void;
}

export default function TermsConsentModal({
  isOpen,
  onAccept,
  onOpenTerms,
  onOpenPrivacy,
}: TermsConsentModalProps) {
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Samtykke kan ikke afvises med Escape: brugeren skal vælge en tydelig handling.
  const { ref: dialogRef } = useDialogA11y({ open: isOpen });

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAccept(analyticsConsent);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Der opstod en fejl under godkendelse af vilkår.',
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      className="terms-consent-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-consent-title"
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.82)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        zIndex: 'calc(var(--z-popover) + 2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        animation: 'fadeIn 0.22s ease',
      }}
    >
      <div
        className="terms-consent-card"
        style={{
          backgroundColor: '#0d1811',
          border: '1px solid rgba(74, 222, 128, 0.35)',
          borderRadius: '18px',
          width: '100%',
          maxWidth: '560px',
          margin: 'auto',
          padding: '28px 24px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
          color: '#e2e8f0',
          fontFamily: 'inherit',
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '22px' }}>
          <div
            style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              backgroundColor: 'rgba(74, 222, 128, 0.15)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4ade80',
              marginBottom: '12px',
              border: '1px solid rgba(74, 222, 128, 0.3)',
            }}
          >
            <ShieldCheck size={28} />
          </div>
          <h2
            id="terms-consent-title"
            style={{
              fontSize: '1.4rem',
              fontWeight: 800,
              color: '#ffffff',
              margin: '0 0 6px 0',
              letterSpacing: '-0.01em',
            }}
          >
            Velkommen til Plotwise
          </h2>
          <p style={{ fontSize: '0.88rem', color: '#94a3b8', margin: 0, lineHeight: 1.45 }}>
            Før du fortsætter til dit revir, beder vi dig bekræfte vores opdaterede
            brugerbetingelser og ansvarsfordeling.
          </p>
        </div>

        {error && (
          <div
            style={{
              backgroundColor: 'rgba(239, 68, 68, 0.15)',
              border: '1px solid rgba(239, 68, 68, 0.4)',
              color: '#fca5a5',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '0.84rem',
              marginBottom: '16px',
            }}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '22px' }}
          >
            {/* Punkt 1 */}
            <div
              style={{
                display: 'flex',
                gap: '14px',
                padding: '12px 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.07)',
              }}
            >
              <div style={{ color: '#4ade80', marginTop: '2px', flexShrink: 0 }}>
                <Lock size={18} />
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.45' }}>
                <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px' }}>
                  1. Dine revirdata er private
                </strong>
                Vi gemmer dine markpolygoner og plejeplaner sikkert i Frankfurt (EU). Vi deler eller
                videresælger aldrig dine oplysninger til tredjepart.
              </div>
            </div>

            {/* Punkt 2 */}
            <div
              style={{
                display: 'flex',
                gap: '14px',
                padding: '12px 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.07)',
              }}
            >
              <div style={{ color: '#4ade80', marginTop: '2px', flexShrink: 0 }}>
                <MapPin size={18} />
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.45' }}>
                <strong style={{ color: '#ffffff', display: 'block', marginBottom: '2px' }}>
                  2. Vejledende planlægning & ansvarsafgrænsning
                </strong>
                Arealer og frøberegninger er vejledende overslag. Du bærer selv ansvaret for kontrol
                af markforhold og maskiner inden såning. Frø købes direkte hos Faunamaster ApS.
              </div>
            </div>

            {/* Punkt 3 */}
            <div
              style={{
                display: 'flex',
                gap: '14px',
                padding: '12px 14px',
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.07)',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center' }}>
                <div style={{ color: '#4ade80', flexShrink: 0 }}>
                  <BarChart3 size={18} />
                </div>
                <div style={{ fontSize: '0.85rem', lineHeight: '1.45' }}>
                  <strong style={{ color: '#ffffff', display: 'block' }}>
                    3. Anonym brugsstatistik
                  </strong>
                  <span style={{ color: '#94a3b8', fontSize: '0.8rem' }}>
                    Hjælper os med at gøre kortet hurtigere og rette fejl.
                  </span>
                </div>
              </div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '44px',
                  minHeight: '44px',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <input
                  type="checkbox"
                  checked={analyticsConsent}
                  onChange={(e) => setAnalyticsConsent(e.target.checked)}
                  style={{
                    width: '18px',
                    height: '18px',
                    cursor: 'pointer',
                    accentColor: '#16a34a',
                  }}
                />
              </label>
            </div>
          </div>

          <div
            style={{
              fontSize: '0.78rem',
              color: '#94a3b8',
              textAlign: 'center',
              marginBottom: '20px',
              lineHeight: 1.45,
            }}
          >
            Ved at klikke nedenfor accepterer du Plotwises{' '}
            <button
              type="button"
              onClick={onOpenTerms}
              style={{
                color: '#4ade80',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              brugerbetingelser & ansvarsafgrænsning
            </button>{' '}
            samt{' '}
            <button
              type="button"
              onClick={onOpenPrivacy}
              style={{
                color: '#4ade80',
                textDecoration: 'underline',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
            >
              privatlivspolitik (GDPR)
            </button>
            .
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              minHeight: '48px',
              borderRadius: '10px',
              backgroundColor: '#15803d',
              border: '1px solid #22c55e',
              color: '#ffffff',
              fontSize: '0.94rem',
              fontWeight: 700,
              cursor: submitting ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(22, 163, 74, 0.4)',
              transition: 'all 0.15s ease',
            }}
          >
            {submitting ? 'Godkender…' : 'Godkend og gå til revir'}
            {!submitting && <ArrowRight size={18} />}
          </button>
        </form>
      </div>
    </div>
  );
}
